import { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { GradeBand } from '@peekapak/types/EmotionsClassification';
import { EmotionsReportClassroom } from '@peekapak/types/EmotionsReportRow';
import { ALL } from './ConcerningEmotionsReportFilters';
import {
  getElementaryMoodColorFill,
  getElementaryMoodTextColor,
  getSecondaryMoodColorFill,
  getSecondaryMoodTextColor,
} from './AdministratorDashboard/MoodboardAdmin/moodboardUtilities';

export interface EmotionColor {
  backgroundColor: string;
  textColor: string;
}

// DateRangePicker returns the selected end date at local midnight - without this,
// check-ins later that same day would be excluded from the range.
export function getEndOfDay(date: Date): number {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.getTime();
}

// A 6-12 check-in's feeling is normally a sub-feeling of the seven-category wheel. Check sub-feelings first,
// since a sub-feeling can share its string with an unrelated Mood (e.g. SecondaryMood.SCARED.NERVOUS ===
// Mood.NERVOUS) - matching the sub-feeling first resolves that collision correctly. Returns undefined, rather
// than throwing, when nothing matches - a Mood value (PK-5's six-mood set) is handled separately by the caller.
function resolveSixToTwelveCategory(feeling: Mood | SecondaryMood): string | undefined {
  const categoryKey = Object.keys(SecondaryMood).find((key) =>
    (Object.values(SecondaryMood[key as keyof typeof SecondaryMood]) as string[]).includes(feeling),
  );
  return categoryKey ? Mood[categoryKey as keyof typeof Mood] : undefined;
}

function pk5Color(feeling: Mood | SecondaryMood): EmotionColor {
  return {
    backgroundColor: getElementaryMoodColorFill(feeling),
    textColor: getElementaryMoodTextColor(feeling),
  };
}

export function getEmotionColor(feeling: Mood | SecondaryMood, gradeBand: GradeBand): EmotionColor {
  if (gradeBand === 'PK-5') {
    return pk5Color(feeling);
  }

  const category = resolveSixToTwelveCategory(feeling);
  if (category) {
    return {
      backgroundColor: getSecondaryMoodColorFill(category),
      textColor: getSecondaryMoodTextColor(category),
    };
  }

  // Not a recognized sub-feeling. A Mood value belongs to PK-5's six-mood set regardless of gradeBand - the
  // seven-category wheel's colors are keyed by its own category names, not PK-5's mood names, so looking up
  // a Mood value there can miss (e.g. "Excited" and "Nervous" aren't wheel category names).
  if ((Object.values(Mood) as string[]).includes(feeling)) {
    return pk5Color(feeling);
  }

  throw new Error(`Invalid feeling: ${feeling}`);
}

const GRADE_LABELS: Record<number, string> = {
  [-1]: 'PK',
  0: 'K',
  1: 'Grade 1',
  2: 'Grade 2',
  3: 'Grade 3',
  4: 'Grade 4',
  5: 'Grade 5',
  6: 'Grade 6',
  7: 'Grade 7',
  8: 'Grade 8',
  9: 'Grade 9',
  10: 'Grade 10',
  11: 'Grade 11',
  12: 'Grade 12',
};

export function getGradeColumnLabel(grade: number): string {
  if (grade === -1) return 'PK';
  if (grade === 0) return 'K';
  return String(grade);
}

export function buildGradeOptions(classrooms: EmotionsReportClassroom[]) {
  const uniqueGrades = [...new Set(classrooms.flatMap((c) => c.students.map((s) => s.grade)))].sort((a, b) => a - b);
  return [
    { id: ALL, name: 'By grade' },
    // Drop any grade with no label instead of exposing the raw number (e.g. bad/legacy data).
    ...uniqueGrades
      .filter((grade) => GRADE_LABELS[grade] !== undefined)
      .map((grade) => ({ id: String(grade), name: GRADE_LABELS[grade] })),
  ];
}
